import * as React from 'react'
import { useMemo } from 'react'
import { pick } from 'lodash'
import connect from './connect'
import CommonElementSelector from './common_element_selector'
import LocationElementSelector from './location_element_selector'
import Preview from './preview'
import SaveButton from '../common/save_button'
import SaveAndGoButton from '../common/save_and_go_button'
import NextButton from '../common/next_button'
import BackButton from '../common/back_button'
import TagsModalSelector from './tags_modal_selector'
import SelectElementsModal from './select_elements_modal'

const { I18n } = window as any

type Tag = {
  selected?: boolean
  value?: string | number
  id: string | number
}

type Element = {
  isSelected: boolean
  isMarkedForDestory?: boolean
  isLocation: boolean
  selectorAttrs: () => any
  prepareDataForSend: () => any
  elementID: string | number
  key: () => string
}

type ImageActions = {
  updateItemImageList: (itemId: string | number, images: any[]) => void
  mergeItemImages: (itemId: string | number, images: any[]) => void
  addNewImageToItem: (itemId: string | number, image: any) => void
  updateItemImage: (itemId: string | number, imageId: string | number, data: any) => void
  removeItemImage: (itemId: string | number, imageId: string | number) => void
}

type ElementsSelectorProps = {
  id: string | number
  isReady: boolean
  isFetching: boolean
  elements: Element[]
  tags: Tag[]
  elementFormData: {
    fields: any[]
  }
  activeItem: any
  // imageDeleteCandidate?: any
  showAdditions?: boolean
  showImages?: boolean
  visibleColumns?: string[]
  ByTagsSelector: React.ComponentType<any>
  selectItems: (items: any[]) => void
  removeItem: (itemId: string | number) => void
  removeItemWithTags: (itemId: string | number) => void
  updateItem: (itemId: string | number, data: any) => void
  updateItemImageList: (itemId: string | number, images: any[]) => void
  mergeItemImages: (itemId: string | number, images: any[]) => void
  addNewImageToItem: (itemId: string | number, image: any) => void
  updateItemImage: (itemId: string | number, imageId: string | number, data: any) => void
  removeItemImage: (itemId: string | number, imageId: string | number) => void
  saveSelected: (
    id: string | number,
    campaignData: any,
    elementsData: any[],
    stepAction?: string | null,
    next?: boolean | null
  ) => void
  selectTags: (itemId: string | number, tags: any[]) => void
  makeCopy: (itemId: string | number) => void
  assignActiveItem: (item: any) => void
  createElement: (campaignId: string | number, data: any) => void
  addAllElements: (filter: { isLocation: boolean }) => void
  selectByTags: (tags: any[], type: string, skipChangesMonitor?: boolean) => void
  // nextStep: (id: string | number) => void
  stepBack: (id: string | number) => void
  uploaderOptions?: any
}

const prepareCampaignData = ({ tags }: { tags: Tag[] }) => {
  return {
    element_tag_ids: tags.filter((t) => t.selected).map((t) => t.value || t.id),
  }
}

const splitElementsByGroups = (items: Element[]) => {
  // notSelectedOther key that mean CommonElement and CampaignElement
  return items.reduce(
    (result, item) => {
      if (item.isSelected) {
        result.allSelectedElements.push(item)
      } else {
        const typedItemKey = item.isLocation ? 'notSelectedLocationElements' : 'notSelectedOtherElements'
        result[typedItemKey].push(item.selectorAttrs())
      }
      return result
    },
    { notSelectedLocationElements: [], notSelectedOtherElements: [], allSelectedElements: [] } as {
      notSelectedLocationElements: any[]
      notSelectedOtherElements: any[]
      allSelectedElements: Element[]
    }
  )
}

function ElementsSelector(props: ElementsSelectorProps) {
  const {
    id,
    elements,
    tags,
    isFetching,
    isReady,
    elementFormData,
    activeItem,
    selectItems,
    removeItem,
    removeItemWithTags,
    updateItem,
    makeCopy,
    assignActiveItem,
    createElement,
    addAllElements,
    selectByTags,
    selectTags,
    saveSelected,
    stepBack,
    ByTagsSelector,
    uploaderOptions,
    showAdditions,
    showImages,
    visibleColumns,
    updateItemImageList,
    mergeItemImages,
    addNewImageToItem,
    updateItemImage,
    removeItemImage,
  } = props

  const handleSave = (e: React.MouseEvent, stepAction: string | null = null, next: boolean | null = null) => {
    const campaignElementsData = elements.reduce((result, el) => {
      if (el.isSelected || el.isMarkedForDestory) {
        result.push(el.prepareDataForSend())
      }
      return result
    }, [] as any[])

    if (campaignElementsData.length > 0) {
      saveSelected(id, prepareCampaignData({ tags }), campaignElementsData, stepAction, next)
    }
  }

  const handleNext = (e: React.MouseEvent) => {
    handleSave(e, null, true)
  }

  const handleBack = () => {
    stepBack(id)
  }

  const saveAndGoToOverview = (e: React.MouseEvent) => {
    handleSave(e, 'save_and_go_to_overview')
  }

  const clickTagsSelectorControl = (item: any) => {
    assignActiveItem(item)
  }

  const resetActiveItem = () => assignActiveItem(null)

  const handleCreateElement = (data: any) => {
    createElement(id, data)
  }

  const handleSelectByTags = (selectedTags: any[]) => {
    selectByTags(selectedTags, 'elements', true)
  }

  const previewImageActions: ImageActions = pick(props, [
    'updateItemImageList',
    'mergeItemImages',
    'addNewImageToItem',
    'updateItemImage',
    'removeItemImage',
  ]) as ImageActions

  const { notSelectedLocationElements, notSelectedOtherElements, allSelectedElements } = splitElementsByGroups(elements)

  // Create a map of tag IDs to full tag objects from campaign tags
  const tagsById = useMemo(() => {
    const map = new Map<string | number, any>()
    tags.forEach((tag: any) => {
      const id = tag.value || tag.id
      if (id) {
        map.set(id, tag)
      }
    })
    return map
  }, [tags])

  const commonElementsForModal = useMemo(() => {
    return elements
      .filter((el: any) => !el.isSelected && !el.isLocation)
      .map((el: any) => {
        // Resolve tagIds to full tag objects
        const resolvedTags = (el.tagIDs || [])
          .map((tagId: string | number) => tagsById.get(tagId))
          .filter(Boolean)
        return {
          value: el.elementID,
          label: el.name,
          name: el.name,
          sku: el.sku,
          type: el._type,
          campaignElementID: el.campaignElementID,
          photoUncuttedThumb: el.photoUncuttedThumb,
          tags: resolvedTags,
          tagIds: el.tagIDs,
        }
      })
  }, [elements, tagsById])

  const allElementTags = useMemo(() => {
    // Collect all unique tag IDs from elements and resolve to full tag objects
    const usedTagIds = new Set<string | number>()
    elements.forEach((el: any) => {
      (el.tagIDs || []).forEach((tagId: string | number) => {
        usedTagIds.add(tagId)
      })
    })
    return Array.from(usedTagIds)
      .map((tagId) => tagsById.get(tagId))
      .filter(Boolean)
  }, [elements, tagsById])

  return (
    <div className="elements-selector">
      <div className="row mb-3">
        <div className="col-12 text-left">
          <SelectElementsModal
            items={commonElementsForModal}
            onAdd={selectItems}
            showImages={showImages}
            isLocation={false}
            allTags={allElementTags}
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-10">
          <CommonElementSelector items={notSelectedOtherElements} selectItems={selectItems} />
        </div>
        <div className="col-2 text-left">
          <button
            type="button"
            className="next btn btn-secondary"
            onClick={() => addAllElements({ isLocation: false })}
          >
            {I18n.t('campaigns.others.add_all_elements')}
          </button>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-10">
          <LocationElementSelector items={notSelectedLocationElements} selectItems={selectItems} />
        </div>
        <div className="col-2 text-left">
          <button type="button" className="next btn btn-secondary" onClick={() => addAllElements({ isLocation: true })}>
            {I18n.t('campaigns.others.add_all_elements')}
          </button>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-10">
          <ByTagsSelector items={tags} selectTag={handleSelectByTags} />
        </div>
        <div className="col-2 text-right"/>
      </div>
      <TagsModalSelector item={activeItem} tags={tags} selectTags={selectTags} resetActiveItem={resetActiveItem} />
      <div className="row">
        <div className="col-12">
          <Preview
            elements={allSelectedElements}
            removeItem={removeItem}
            removeItemWithTags={removeItemWithTags}
            updateItem={updateItem}
            imageActions={previewImageActions}
            makeCopy={makeCopy}
            clickTagsSelectorControl={clickTagsSelectorControl}
            // uploaderOptions={uploaderOptions}
            showAdditions={showAdditions}
            showImages={showImages}
            visibleColumns={visibleColumns}
          />
        </div>
      </div>
      <div className="row justify-content-start">
        <div className="col-1">
          <BackButton onClick={handleBack} />
        </div>
        <div className="col-0">
          <SaveButton onClick={handleSave} />
        </div>
        {isReady && (
          <div className="col-3">
            <SaveAndGoButton onClick={saveAndGoToOverview} />
          </div>
        )}
        <div className="col-1">
          <NextButton onClick={handleNext} />
        </div>
      </div>
    </div>
  )
}

export default connect(ElementsSelector)
